import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';

import {
  Logo,
  Schedule,
  ShortInterestParams,
  ShortInterestResponse,
  ShortInterestsDataSet,
  TickerDetailsResponse,
  DetailedQuotesBySymbol,
  GetQuotesLogoParams,
  DelayedQuote,
  TickerDetail,
  BullBearStatements,
  QuoteAnalysisResponse,
  PeersResponse,
} from './entities';
import {
  QuotesScheduleRestful,
  QuotesDelayedRestful,
  QuotesTickerRestful,
  InterestRestful,
  DetailedQuotesRestful,
  QuotesLogosRestful,
  QuotesBullSayBearSayRestful,
  QuotesPeersRestful,
  //QuotesSymbolRestful,
} from './restful';
import { Session } from '@benzinga/session';
import { StockSymbol } from '@benzinga/session';
import { isArray } from '@benzinga/utils';
import { QuoteAnalysisRestful } from './restful/analysis';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'get_detailed_quotes_error'
    | 'get_logos_error'
    | 'get_delayed_quotes_error'
    | 'get_ticker_details_error'
    | 'get_short_interest_error'
    | 'get_peers_error';
  type: 'error';
}

interface WatchlistQuotesEvent {
  type: 'get_detailed_quotes';
  watchlistQuotes: DetailedQuotesBySymbol;
}

interface ShortInterestEvent {
  type: 'get_short_interest';
  shortInterest: ShortInterestResponse;
}

interface LogoQuotesEvent {
  logos: Logo[];
  type: 'get_quotes_logos';
}

interface DelayedQuotesEvent {
  quotes: Record<StockSymbol, DelayedQuote>;
  type: 'get_delayed_quotes';
}
interface TickerDetailsEvent {
  result: TickerDetail[];
  type: 'get_ticker_details';
}

interface PeersEvent {
  peersResponse: PeersResponse;
  type: 'get_peers';
}

export type WatchlistQuotesRequestEvent =
  | ErrorEvent
  | WatchlistQuotesEvent
  | LogoQuotesEvent
  | DelayedQuotesEvent
  | TickerDetailsEvent
  | ShortInterestEvent
  | PeersEvent;

interface WatchlistQuotesFunctions {
  getDelayedQuotes: WatchlistQuotesRequest['getDelayedQuotes'];
  getDetailedQuotes: WatchlistQuotesRequest['getDetailedQuotes'];
  getPeers: WatchlistQuotesRequest['getPeers'];
  getQuotesLogos: WatchlistQuotesRequest['getQuotesLogos'];
  getShortInterest: WatchlistQuotesRequest['getShortInterest'];
  getTickerDetails: WatchlistQuotesRequest['getTickerDetails'];
}

export class WatchlistQuotesRequest extends ExtendedListenableSubscribable<
  WatchlistQuotesRequestEvent,
  WatchlistQuotesFunctions
> {
  //private restful: QuotesSymbolRestful;
  private scheduleRestful: QuotesScheduleRestful;
  private delayedRestful: QuotesDelayedRestful;
  private tickerRestful: QuotesTickerRestful;
  private interestRestful: InterestRestful;
  private detailedQuotesRestful: DetailedQuotesRestful;
  private logosRestful: QuotesLogosRestful;
  private bullSayBearSayRestful: QuotesBullSayBearSayRestful;
  private analysisRestful: QuoteAnalysisRestful;
  private peersRestful: QuotesPeersRestful;

  constructor(session: Session) {
    super();
    //this.restful = new QuotesSymbolRestful(session);
    this.scheduleRestful = new QuotesScheduleRestful(session);
    this.delayedRestful = new QuotesDelayedRestful(session);
    this.detailedQuotesRestful = new DetailedQuotesRestful(session);
    this.tickerRestful = new QuotesTickerRestful(session);
    this.interestRestful = new InterestRestful(session);
    this.logosRestful = new QuotesLogosRestful(session);
    this.bullSayBearSayRestful = new QuotesBullSayBearSayRestful(session);
    this.analysisRestful = new QuoteAnalysisRestful(session);
    this.peersRestful = new QuotesPeersRestful(session);
  }

  public getDetailedQuotes = async (symbols: StockSymbol[]): SafePromise<DetailedQuotesBySymbol> => {
    const watchlistQuotes = await this.detailedQuotesRestful.getDetailedQuotes(symbols.toString());

    if (watchlistQuotes.err) {
      this.dispatch({
        error: watchlistQuotes.err,
        errorType: 'get_detailed_quotes_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'get_detailed_quotes',
        watchlistQuotes: watchlistQuotes.ok,
      });
    }
    return watchlistQuotes;
  };

  public getQuotesLogos = async (
    symbols: StockSymbol[],
    params?: GetQuotesLogoParams,
  ): SafePromise<{ data: Logo[] }> => {
    const logos = await this.logosRestful.getQuotesLogos(symbols.toString(), params);

    if (logos.err) {
      this.dispatch({
        error: logos.err,
        errorType: 'get_logos_error',
        type: 'error',
      });
      return logos;
    } else {
      this.dispatch({
        logos: logos.ok.data,
        type: 'get_quotes_logos',
      });
      return logos;
    }
  };

  public getDelayedQuotes = async (symbols: StockSymbol[]): SafePromise<Record<StockSymbol, DelayedQuote>> => {
    const delayedQuotes = await this.delayedRestful.getDelayedQuotes(symbols.toString());

    if (delayedQuotes.err) {
      this.dispatch({
        error: delayedQuotes.err,
        errorType: 'get_delayed_quotes_error',
        type: 'error',
      });
      return { err: delayedQuotes.err };
    } else {
      this.dispatch({
        quotes: delayedQuotes.ok,
        type: 'get_delayed_quotes',
      });

      return delayedQuotes;
    }
  };

  public getTickerDetails = async (symbols: StockSymbol[]): SafePromise<TickerDetailsResponse> => {
    const tickerDetails = await this.tickerRestful.getTickerDetails(symbols.toString());

    if (tickerDetails.err) {
      this.dispatch({
        error: tickerDetails.err,
        errorType: 'get_ticker_details_error',
        type: 'error',
      });
      return { err: tickerDetails.err };
    } else {
      this.dispatch({
        result: tickerDetails.ok.result,
        type: 'get_ticker_details',
      });

      return tickerDetails;
    }
  };

  public getSchedule = async (): SafePromise<Schedule> => {
    return await this.scheduleRestful.getSchedule();
  };

  public getShortInterest = async (params: ShortInterestParams): SafePromise<ShortInterestsDataSet> => {
    const shortInterests = await this.interestRestful.getShortInterests(params);

    if (shortInterests.err) {
      this.dispatch({
        error: shortInterests.err,
        errorType: 'get_short_interest_error',
        type: 'error',
      });

      return { err: shortInterests.err };
    } else {
      this.dispatch({
        shortInterest: shortInterests.ok,
        type: 'get_short_interest',
      });
    }

    return { ok: shortInterests?.ok?.shortInterestData };
  };

  public getBullSayBearSayStatements = async (symbols: string[]): SafePromise<BullBearStatements[]> => {
    const statements = await this.bullSayBearSayRestful.getStatements(symbols);
    if (statements.err) {
      return { err: statements.err };
    } else if (isArray(statements.ok)) {
      return { err: new SafeError('No results', 'No results') };
    } else {
      return { ok: statements.ok.bulls_say_bears_say };
    }
  };

  public getQuoteAnalysis = async (symbol: string): SafePromise<QuoteAnalysisResponse> => {
    return this.analysisRestful.getQuoteAnalysis(symbol);
  };

  public getPeers = async (symbol: StockSymbol): SafePromise<PeersResponse> => {
    const peersResponse = await this.peersRestful.getPeers(symbol);

    if (peersResponse.err) {
      this.dispatch({
        error: peersResponse.err,
        errorType: 'get_peers_error',
        type: 'error',
      });
      return { err: peersResponse.err };
    } else {
      this.dispatch({
        peersResponse: peersResponse.ok,
        type: 'get_peers',
      });
      return peersResponse;
    }
  };

  protected onSubscribe(): WatchlistQuotesFunctions {
    return {
      getDelayedQuotes: this.getDelayedQuotes,
      getDetailedQuotes: this.getDetailedQuotes,
      getPeers: this.getPeers,
      getQuotesLogos: this.getQuotesLogos,
      getShortInterest: this.getShortInterest,
      getTickerDetails: this.getTickerDetails,
    };
  }
}
